import React, { useState, useRef, useEffect, useCallback } from "react";
import { Brain, Send, Sparkles, TrendingUp, Database, BarChart3, Table2, Eye, X as XIcon, CheckCircle2, Shield, ThumbsDown, ChevronRight, Trash2, RefreshCw, AlertTriangle, Info } from "lucide-react";
import ProjectBar from "../components/ProjectBar";
import { useProjects } from "../ProjectContext";
import { useFeature } from "../featureFlags";
import AutoDeck from "../insights/AutoDeck";
import { ToastView, ConfirmDialog } from "../components/feedback";
import { BarChart, Bar, LineChart, Line, ComposedChart, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Customized } from "recharts";
import { getFreshToken } from "../api";
import { PALETTE } from "../insights/designTokens";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// Single display-side source of truth for every chat chart/table number (percent ×100 once,
// adaptive precision, ratio Nx, currency/number compact-or-full). See utils/formatMetric.js.
import { formatMetric } from "../utils/formatMetric";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");
// Chat charts share the platform default palette (CFO executive) with the Auto-Insights board;
// a customer's brand palette (when set) overrides via the `palette` prop.
const CHART_COLORS = PALETTE;

// Legible chart-tooltip styling — a crisp white card with a DARK, bold title (Recharts' default
// label is a faint gray that was hard to read, e.g. long campaign names) and tidy rows. Item text
// keeps each series' color (matches the bars/legend); only the card + title are restyled.
const TOOLTIP_CONTENT_STYLE = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", boxShadow: "0 8px 24px rgba(15,23,42,.14)", fontSize: "12.5px", padding: "8px 12px" };
const TOOLTIP_LABEL_STYLE = { color: "#0f172a", fontWeight: 700, marginBottom: "4px" };
// Force ROW TEXT to a dark, readable color (Recharts colors item text by the series color, which
// made light series like a gray "roas" line barely legible). The colored marker next to each row
// still preserves the series association.
const TOOLTIP_ITEM_STYLE = { padding: "1px 0", fontWeight: 600, color: "#1e293b" };

// Small, quiet control style for the chat-memory actions (New chat / Clear history).
const memoryBtnStyle = {
  display: "inline-flex", alignItems: "center", gap: "5px", padding: "5px 10px", borderRadius: "8px",
  border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: "11.5px", fontWeight: 600,
  cursor: "pointer", whiteSpace: "nowrap",
};

// ─── Display formatting (shared by table view, source records, and PDF export) ──
const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Columns whose numeric values represent money → render with a $ and thousands separators.
// Includes Spanish money terms (ganancia/ingreso/ventas/importe/monto/precio/costo/gasto/
// valor/beneficio/utilidad) so a Spanish dataset formats as money too. Used ONLY for the
// no-meta fallback; the authoritative source is the backend column_meta.
const CURRENCY_HINTS = ["revenue", "amount", "cost", "price", "sales", "margin", "cogs", "paid", "balance", "fee", "gross", "net", "spend", "profit",
  "ganancia", "ingreso", "ventas", "importe", "monto", "precio", "costo", "coste", "gasto", "facturacion", "beneficio", "utilidad", "saldo", "valor"];

function _looksLikeDate(v) {
  // YYYY-MM-DD, optionally followed by a time component (e.g. "2025-12-29 00:00:00.000")
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?Z?)?$/.test(v.trim());
}

// Friendly period label for X ticks / category labels: "2025-01" → "Jan 2025",
// "2025-01-06" → "Jan 6, 2025", "2025-Q1" → "Q1 2025", "2025" → "2025". Any non-period
// value (a plain category, a name) is returned unchanged. Display-only, deterministic.
function _friendlyPeriod(v) {
  const s = String(v == null ? "" : v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);           // full date
  if (m) { const mi = +m[2] - 1; if (mi >= 0 && mi < 12) return `${_MONTHS[mi]} ${+m[3]}, ${m[1]}`; }
  m = s.match(/^(\d{4})-(\d{2})$/);                       // year-month
  if (m) { const mi = +m[2] - 1; if (mi >= 0 && mi < 12) return `${_MONTHS[mi]} ${m[1]}`; }
  m = s.match(/^(\d{4})-?Q([1-4])$/i);                    // year-quarter
  if (m) return `Q${m[2]} ${m[1]}`;
  return s;
}
// Compact-magnitude axis ticks ("80k" / "1.2M") now live in the shared formatMetric module (the
// single display authority) and reach the charts via formatMetric(..., { compact: true }); the
// former local `_compactNum` was removed when its two call sites were routed through formatMetric.

// Fast chart capture for PDF export: serialize the Recharts <svg> straight to a PNG instead of
// html2canvas (which clones the DOM + re-parses every app stylesheet to rasterize one chart —
// the cause of the multi-second "PDF is taking forever"). Near-instant, and pixel-faithful to
// what's on screen. Returns {dataUrl, w, h} or null. (The HTML legend lives OUTSIDE the svg, so
// exportToPDF draws it separately.)
function _hexToRgb(h) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(h || ""));
  if (!m) return [99, 102, 241];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
async function svgChartToPng(container, scale = 2) {
  const svg = container && container.querySelector("svg");
  if (!svg) return null;
  const rect = svg.getBoundingClientRect();
  const w = Math.max(1, Math.ceil(rect.width)), h = Math.max(1, Math.ceil(rect.height));
  const clone = svg.cloneNode(true);
  clone.setAttribute("width", String(w));
  clone.setAttribute("height", String(h));
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.style.fontFamily = "Arial, Helvetica, sans-serif";
  const xml = new XMLSerializer().serializeToString(clone);
  const src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
  const img = new Image();
  await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = src; });
  const canvas = document.createElement("canvas");
  canvas.width = w * scale; canvas.height = h * scale;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  ctx.drawImage(img, 0, 0, w, h);
  return { dataUrl: canvas.toDataURL("image/png"), w, h };
}

function _friendlyDate(v) {
  const m = String(v).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(v);
  const mi = parseInt(m[2], 10) - 1;
  if (mi < 0 || mi > 11) return String(v);
  return `${_MONTHS[mi]} ${parseInt(m[3], 10)}, ${m[1]}`;
}

function _isCurrencyColumn(col) {
  const n = String(col).toLowerCase();
  return CURRENCY_HINTS.some(h => n.includes(h));
}

// Numeric columns that are identifiers/codes, not metrics — mirrors the backend
// ai_insights._is_id_like so an ID (e.g. line_item_id) is never shown as a headline value.
function _isIdLikeColumn(col) {
  const n = String(col).toLowerCase().trim();
  if (n === "id" || n.endsWith("_id") || n.startsWith("id_")) return true;
  return ["uuid", "guid", "phone", "zip", "postal", "fax"].some(h => n.includes(h));
}

// Roles that can be a headline value (mirror app.column_semantics).
const _METRIC_ROLES = ["currency", "percent", "ratio", "measure"];
// Choose the headline metric from the authoritative column meta: a real metric (currency
// preferred), NEVER an id/date/categorical. Returns a column name or null.
function _pickHeadlineMetric(meta, keys) {
  if (!meta) return null;
  for (const role of _METRIC_ROLES) {
    const hit = keys.find(k => meta[k] && meta[k].role === role);
    if (hit) return hit;
  }
  return null;
}

function _formatCurrency(value) {
  const s = String(value).trim();
  if (!/^-?\d[\d,]*\.?\d*$/.test(s)) return String(value);
  const num = parseFloat(s.replace(/,/g, ""));
  if (isNaN(num)) return String(value);
  const opts = Number.isInteger(num)
    ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return "$" + num.toLocaleString("en-US", opts);
}

// Format a plain numeric measure with thousands separators and at most 2 decimals, so a raw
// value like 760620.3699999906 renders as "760,620.37" instead of a full-precision float.
function _formatNumber(value) {
  const s = String(value).trim();
  if (!/^-?\d[\d,]*\.?\d*$/.test(s)) return String(value);
  const num = parseFloat(s.replace(/,/g, ""));
  if (isNaN(num)) return String(value);
  const opts = Number.isInteger(num)
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return num.toLocaleString("en-US", opts);
}

// Format a single cell for display. Prefers the AUTHORITATIVE column meta from the backend
// (app.column_semantics) so an ID/date is never shown as money and "$" is role-driven, never
// magnitude-driven. `meta` is the per-column {role, format} (optional — older/restored
// messages fall back to the conservative name-based heuristics).
function formatCell(col, value, meta) {
  if (value === null || value === undefined || value === "") return "";
  const fmt = meta && meta.format;
  if (fmt) {
    // Route EVERY typed cell through the shared formatMetric (the single display authority) so a
    // 'percent' fraction renders ×100 with adaptive precision (0.0032 → "0.32%", not 16 raw
    // decimals), 'percent_scaled' as-is-with-% (no ×100), and 'ratio' as "Nx" — while currency /
    // number / date keep EXACTLY their prior rendering. text/IDs still pass through untouched.
    if (fmt === "currency" || fmt === "number" || fmt === "int" || fmt === "date"
        || fmt === "percent" || fmt === "percent_scaled" || fmt === "ratio") {
      return formatMetric(value, fmt);   // full (non-compact) for table/PDF cells
    }
    // text (incl. IDs) and any unknown format → shown as-is (NEVER a spurious "$").
    return String(value);
  }
  // Fallback: no meta (e.g. a restored history turn) → name-based, conservative.
  if (_looksLikeDate(value)) return _friendlyDate(value);
  if (_isCurrencyColumn(col) && !_isIdLikeColumn(col)) return _formatCurrency(value);
  // Any other plain number still gets thousands separators (never a full-precision float),
  // but non-numeric text is returned untouched.
  if (!_isIdLikeColumn(col) && /^-?\d[\d,]*\.?\d+$/.test(String(value).trim())) return _formatNumber(value);
  return String(value);
}

// Red/green for "delta" columns (change/growth/variance/margin/…) or values carrying an
// explicit +/- sign. Returns a CSS color or null (leave default). Plain magnitudes like a
// revenue of "5000" are NOT colored — only signed/delta values.
const DELTA_HINTS = ["delta", "change", "growth", "variance", "diff", "margin", "profit", "gain", "loss", "yoy", "mom", "Δ", "net", " vs", "vs "];
function cellTextColor(col, value) {
  if (value === null || value === undefined || value === "") return null;
  const s = String(value).trim();
  const n = String(col).toLowerCase();
  const looksDelta = DELTA_HINTS.some(h => n.includes(h)) || /^[+\-]/.test(s) || /^\(.*\)$/.test(s);
  if (!looksDelta) return null;
  const negParen = /^\(.*\)$/.test(s); // accounting-style negative e.g. (922)
  const num = parseFloat(s.replace(/[$,%()\s]/g, ""));
  if (isNaN(num)) return null;
  if (num < 0 || negParen || /^-/.test(s)) return "#dc2626"; // red
  if (num > 0) return "#16a34a"; // green
  return null; // zero → neutral
}

// Auto-retry fetch: refreshes token on 401 AND retries transient network
// failures (browser "Failed to fetch", e.g. Lambda cold-start blips) with backoff.
async function authFetch(url, options = {}, idToken, maxNetworkRetries = 2) {
  let token = idToken;
  const doFetch = (t) =>
    fetch(url, { ...options, headers: { ...options.headers, Authorization: `Bearer ${t}` } });

  let lastErr;
  for (let attempt = 0; attempt <= maxNetworkRetries; attempt++) {
    try {
      let res = await doFetch(token);
      if (res.status === 401) {
        token = await getFreshToken();
        res = await doFetch(token);
      }
      return res;
    } catch (err) {
      // fetch() rejects (TypeError) only on network-level failures, not HTTP errors.
      lastErr = err;
      if (attempt < maxNetworkRetries) {
        await new Promise((r) => setTimeout(r, 700 * (attempt + 1))); // 700ms, then 1400ms
        continue;
      }
    }
  }
  throw lastErr;
}

const EXAMPLE_QUESTIONS = [
  "How many records do I have in total?",
  "What are the top 5 lead sources?",
  "Show me the breakdown by status",
  "What's the average lead value?",
];

// ─── "Added to board" signature (match a chat chart to a persisted pinned visual) ──────────
// Mirrors the backend store.custom_signature (title|type|x|series) + the addToBoard payload,
// so the chat's "✓ Added" state is DERIVED from the real board, accurate across reloads.
const _PIN_TYPEMAP = { pie: "donut", doughnut: "donut", donut: "donut", area: "line", line: "line", bar: "bar" };
function _pinSignature(title, type, x, series) {
  return `${String(title || "").slice(0, 160).trim().toLowerCase()}|${type || "bar"}|${x || ""}|${(series || []).slice(0, 6).map(String).join(",")}`;
}
function chatChartSignature(chart, question, rawData) {
  if (!chart) return "";
  const isCombo = chart.type === "combo" || chart.type === "stacked_bar";
  const type = isCombo ? chart.type : (_PIN_TYPEMAP[String(chart.type)] || "bar");
  const first = (rawData && rawData[0]) || (Array.isArray(chart.data) && chart.data[0]) || {};
  const series = (chart.y_keys && chart.y_keys.length) ? chart.y_keys
    : Object.keys(first).filter((k) => k !== chart.x_key).slice(0, 3);
  return _pinSignature(chart.title || question || "Custom view", type, chart.x_key || "", series);
}
function pinnedVisualSignature(v) {
  const s = (v && v.chart_spec) || {};
  return _pinSignature(v && v.title, s.type, s.x, s.series);
}

// Minimal rich text for assistant answers: renders **bold** segments and preserves line
// breaks. Used so the deterministic "why not a combined chart" reason can be emphasized
// (the backend wraps that sentence in **...**). No external markdown dependency.
function RichText({ text }) {
  const s = String(text == null ? "" : text);
  const lines = s.split("\n");
  return lines.map((line, li) => (
    <React.Fragment key={li}>
      {line.split(/(\*\*[^*]+\*\*)/g).map((seg, si) => {
        const m = seg.match(/^\*\*([^*]+)\*\*$/);
        return m ? <strong key={si}>{m[1]}</strong> : <React.Fragment key={si}>{seg}</React.Fragment>;
      })}
      {li < lines.length - 1 && <br />}
    </React.Fragment>
  ));
}

export default function AIInsightsPage({ idToken, messages, setMessages, msgOwnerRef }) {
  const [input, setInput] = useState("");
  const [toast, setToast] = useState(null);        // {type, title, message}
  const toastTimerRef = useRef(null);
  const [boardAdds, setBoardAdds] = useState({});  // msgIndex -> "adding" | "added" (button state)
  const addInFlightRef = useRef(new Set());        // synchronous guard vs rapid double-clicks
  // Show a styled in-app toast (auto-dismiss). Replaces alert() everywhere on this page.
  const notify = (type, title, message = "") => {
    setToast({ type, title, message, key: Date.now() });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 4800);
  };
  // In-app confirm dialog (replaces window.confirm). askConfirm(opts) opens it; the
  // dialog stays open until the user picks. opts: {title, message, confirmLabel, tone, onConfirm}.
  const [confirmState, setConfirmState] = useState(null); // {title, message, confirmLabel, tone, onConfirm}
  const askConfirm = (opts) => setConfirmState(opts);
  const [loading, setLoading] = useState(false);
  const [showData, setShowData] = useState(null); // index of message to show raw data
  const [flagged, setFlagged] = useState({}); // answers the user flagged as unhelpful
  const [suggestedInsights, setSuggestedInsights] = useState(null); // null = loading, [] = no data
  const [activeInsight, setActiveInsight] = useState(null); // index of selected insight
  const [alerts, setAlerts] = useState([]); // anomaly alerts
  // Right Insight Engine panel — open by default, but remembers the user's last choice
  // across tab switches / reloads (persisted per browser).
  const [showInsights, setShowInsights] = useState(() => {
    try { const v = localStorage.getItem("boomerang_insight_engine_open"); return v === null ? true : v === "1"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("boomerang_insight_engine_open", showInsights ? "1" : "0"); } catch { /* ignore */ }
  }, [showInsights]);
  const [aiUsage, setAiUsage] = useState(null); // {usage_usd, budget_usd, budget_remaining}
  const [dataFreshness, setDataFreshness] = useState(null); // ISO timestamp of most recent upload
  const [freshnessLoaded, setFreshnessLoaded] = useState(false); // data-version resolved for the active project?
  const [boardRefresh, setBoardRefresh] = useState(0); // bumped after "Add to board" so AutoDeck refetches
  const [pinnedSigs, setPinnedSigs] = useState({});    // signature -> customId, from the persisted board
  const [brandPalette, setBrandPalette] = useState(null); // customer brand colors (from AutoDeck) → chat charts
  const messagesEndRef = useRef(null);

  // ─── Chat memory (R18/R19) ────────────────────────────────────────────────
  // The active project drives which saved conversation is shown. Switching projects
  // loads that project's thread and never blends context across projects.
  const { activeProjectId } = useProjects();
  // Rollout valve (R21): when ON, AI Insights leads with the auto-generated executive board;
  // when OFF this is exactly today's experience (no board, no new calls).
  const autoDeckOn = useFeature("auto_insights_board");
  const brandColorsOn = useFeature("auto_insights_brand_colors"); // independent rollout valve (R22)
  const [chatMeta, setChatMeta] = useState({ enabled: true, stale: false }); // memory on/off + data-changed flag
  // Load the active project's saved conversation. Uses msgOwnerRef (kept in App, so it
  // survives this page's unmount/remount): if the recorded owner project differs from the
  // active project, the active project changed — possibly WHILE this page was unmounted,
  // e.g. after archiving the active project — so reload the correct thread instead of
  // leaving the previous project's history on screen. On first mount with no in-session
  // messages, restore; a same-project remount keeps the live conversation.
  useEffect(() => {
    if (!idToken || !activeProjectId) return;
    const prevOwner = msgOwnerRef ? msgOwnerRef.current : null;
    const isSwitch = prevOwner !== null && prevOwner !== activeProjectId;
    const isInitial = prevOwner === null;
    if (msgOwnerRef) msgOwnerRef.current = activeProjectId;
    let cancelled = false;
    // Pin the request to the project selected in THIS effect run. The global fetch
    // wrapper injects X-Project-Id from a module var that ProjectContext updates in a
    // separate (parent) effect which runs AFTER this one — so relying on it would send
    // the previous project's id and load the wrong (stale) thread on switch. Sending it
    // explicitly wins (the wrapper only fills it when absent).
    const pid = activeProjectId;
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/ai/chat/thread`, { method: "GET", headers: { "X-Project-Id": pid } }, idToken);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        setChatMeta({ enabled: !!data.enabled, stale: !!data.stale });
        const restored = [];
        for (const t of (data.turns || [])) {
          restored.push({ role: "user", content: t.question });
          restored.push({ role: "assistant", content: t.answer, chart: t.chart || undefined });
        }
        // INVARIANT (chat is never wiped by a board action): the on-screen conversation is
        // REPLACED only on a genuine project switch (show that project's own saved thread), or
        // ADOPTED on first mount when there's no live conversation AND the server actually has
        // history. It is NEVER emptied as a side effect of a board Regenerate/refresh/domain
        // change, a re-render, or an unexpectedly-empty server read on the SAME project — those
        // must leave the live conversation untouched. (A board rebuild does not touch chat.)
        if (isSwitch) {
          setMessages(restored);                     // switching projects → that project's thread (may be empty)
        } else if (isInitial && messages.length === 0 && restored.length > 0) {
          setMessages(restored);                     // adopt saved history; never overwrite a live convo with []
        }
      } catch (e) { console.warn("chat thread restore failed", e); }
    })();
    return () => { cancelled = true; };
  }, [idToken, activeProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNewChat = async () => {
    try { await authFetch(`${API_URL}/ai/chat/new-session`, { method: "POST", headers: { "X-Project-Id": activeProjectId } }, idToken); } catch (e) { /* ignore */ }
    setMessages([]);
    setChatMeta((m) => ({ ...m, stale: false }));
  };

  const clearChatHistory = () => {
    askConfirm({
      title: "Clear conversation history?",
      message: "This permanently deletes all of this project's saved conversations for everyone on your account. This can't be undone.",
      confirmLabel: "Clear history",
      tone: "danger",
      onConfirm: async () => {
        setConfirmState(null);
        try { await authFetch(`${API_URL}/ai/chat/thread`, { method: "DELETE", headers: { "X-Project-Id": activeProjectId } }, idToken); } catch (e) { /* ignore */ }
        setMessages([]);
        setChatMeta((m) => ({ ...m, stale: false }));
        notify("success", "History cleared", "This project's saved conversations have been removed.");
      },
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages, loading]);

  // Clear _isNew flags on mount (prevents re-animation on tab switch)
  useEffect(() => {
    if (messages.some(m => m._isNew)) {
      setMessages(prev => prev.map(m => ({ ...m, _isNew: false })));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch dynamic suggested insights, tied to the project's DATA-VERSION (the most recent
  // file change). We wait until the data-version is resolved (freshnessLoaded) so we query
  // with the correct version, and cache the result per (project, data-version) in
  // sessionStorage. Effect: suggestions auto-refresh after an upload/move changed the data
  // (new version → cache miss → refetch), while returning to an UNCHANGED project serves the
  // cache — so we no longer fire a Claude call on every visit (staleness fixed + cheaper).
  useEffect(() => {
    if (!idToken || !activeProjectId || !freshnessLoaded) return;
    const version = dataFreshness || "none";
    const cacheKey = `bmrg.suggest.${activeProjectId}.${version}`;
    let cached = null;
    try { cached = sessionStorage.getItem(cacheKey); } catch (e) { cached = null; }
    if (cached !== null) {
      try { setSuggestedInsights(JSON.parse(cached)); return; } catch (e) { /* refetch */ }
    }
    setSuggestedInsights(null); // "refreshing" skeleton only when this data-version is new
    const fetchInsights = async () => {
      try {
        const res = await fetch(`${API_URL}/ai/suggested-insights`, {
          headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": activeProjectId }
        });
        const list = res.ok ? (await res.json()) : null;
        const insights = list && list.has_data ? list.insights : [];
        setSuggestedInsights(insights);
        try { sessionStorage.setItem(cacheKey, JSON.stringify(insights)); } catch (e) { /* ignore quota */ }
      } catch (e) {
        console.warn("Failed to fetch suggested insights", e);
        setSuggestedInsights([]);
      }
    };
    fetchInsights();
  }, [idToken, activeProjectId, dataFreshness, freshnessLoaded]); // refetch when data-version changes

  // Fetch anomaly alerts
  useEffect(() => {
    if (!idToken) return;
    const fetchAlerts = async () => {
      try {
        const res = await fetch(`${API_URL}/ai/alerts`, {
          headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": activeProjectId }
        });
        if (res.ok) {
          const data = await res.json();
          setAlerts(data.alerts || []);
        }
      } catch (e) { console.warn("Failed to fetch alerts", e); }
    };
    fetchAlerts();
  }, [idToken, activeProjectId]); // re-fetch when the active project changes

  // Fetch AI usage/budget
  useEffect(() => {
    if (!idToken) return;
    const fetchUsage = async () => {
      try {
        const res = await fetch(`${API_URL}/ai/usage`, { headers: { Authorization: `Bearer ${idToken}` } });
        if (res.ok) setAiUsage(await res.json());
      } catch (e) { console.warn("Failed to fetch AI usage", e); }
    };
    fetchUsage();
  }, [idToken]);

  // Fetch data freshness (most recent file change) — this is the project's DATA-VERSION,
  // which also drives the suggested-insights cache/refresh above. Reset on project switch so
  // a previous project's timestamp can never linger (and never seed a wrong-version fetch).
  useEffect(() => {
    if (!idToken) return;
    setFreshnessLoaded(false);
    setDataFreshness(null);
    const fetchFreshness = async () => {
      try {
        const res = await fetch(`${API_URL}/upload/files`, { headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": activeProjectId } });
        if (res.ok) {
          const data = await res.json();
          const files = data.files || [];
          if (files.length > 0) {
            const mostRecent = files.reduce((latest, f) => f.last_modified > latest ? f.last_modified : latest, files[0].last_modified);
            setDataFreshness(mostRecent);
          }
        }
      } catch (e) {
        console.warn("Failed to fetch data freshness", e);
      } finally {
        setFreshnessLoaded(true); // data-version resolved (even if no files) → suggestions may load
      }
    };
    fetchFreshness();
  }, [idToken, activeProjectId]); // re-fetch when the active project changes

  const dismissAlert = async (alertId) => {
    try {
      await fetch(`${API_URL}/ai/alerts/${alertId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${idToken}` }
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) { console.warn("Failed to dismiss alert", e); }
  };

  const runAnomalyDetection = async () => {
    try {
      const res = await fetch(`${API_URL}/ai/detect-anomalies`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.alerts && data.alerts.length > 0) {
          setAlerts(data.alerts);
        }
      }
    } catch (e) { console.warn("Anomaly detection failed", e); }
  };

  // Poll for async query result (long-running queries that exceed the 30s sync limit)
  const pollForResult = async (queryId, maxAttempts = 60) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(r => setTimeout(r, 2000)); // poll every 2s
      try {
        const res = await authFetch(`${API_URL}/ai/ask-result/${queryId}`, { method: "GET" }, idToken);
        if (!res.ok) continue;
        const data = await res.json();
        if (data.status !== "running") {
          return data; // complete (or errored) — return final result
        }
        // still running — keep polling
      } catch (e) {
        console.warn("Poll attempt failed", e);
      }
    }
    // Timed out after maxAttempts × 2s (= 2 min)
    return { answer: "This query is taking longer than expected. Please try a simpler question or try again.", error: "poll_timeout" };
  };

  const askQuestion = async (question, insightIndex = null) => {
    if (!question.trim() || loading) return;
    
    if (insightIndex !== null) setActiveInsight(insightIndex);
    
    const userMsg = { role: "user", content: question };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await authFetch(`${API_URL}/ai/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          conversation_history: messages.slice(-6).map(m => ({
            role: m.role,
            content: m.content,
            sql: m.sql || undefined,
          }))
        })
      }, idToken);

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to get response");
      }

      const data = await res.json();

      // Async long query — poll until complete
      if (data.status === "running" && data.pending_query_id) {
        const finalData = await pollForResult(data.pending_query_id);
        if (finalData) {
          const aiMsg = {
            role: "assistant", content: finalData.answer, sql: finalData.sql_query,
            rawData: finalData.raw_data, chart: finalData.chart,
            displayPreference: finalData.display_preference || "auto",
            tablesUsed: finalData.tables_used, followUps: finalData.follow_up_questions || [],
            columnMeta: finalData.column_meta || null,
            error: finalData.error, _isNew: true,
          };
          setMessages(prev => [...prev.map(m => ({ ...m, _isNew: false })), aiMsg]);
          return;
        }
      }

      const aiMsg = {
        role: "assistant",
        content: data.answer,
        sql: data.sql_query,
        rawData: data.raw_data,
        chart: data.chart,
        displayPreference: data.display_preference || "auto",
        tablesUsed: data.tables_used,
        followUps: data.follow_up_questions || [],
        columnMeta: data.column_meta || null,
        error: data.error,
        _isNew: true,
      };
      setMessages(prev => [...prev.map(m => ({ ...m, _isNew: false })), aiMsg]);
    } catch (err) {
      // Customer-facing, friendly message (never expose backend/AWS internals — roadmap #10).
      // A 504/timeout or network blip surfaces here as a generic fetch failure; guide the user
      // to retry or narrow the question rather than showing a developer error.
      const raw = (err && err.message) ? String(err.message) : "";
      const looksTimeout = /failed to (get|fetch)|timeout|timed out|network|504|502|gateway/i.test(raw);
      const friendly = looksTimeout
        ? "That question took longer than expected and may still be processing. Please try again, or narrow it a bit (for example, one metric or one time period)."
        : "Something went wrong answering that question. Please try again in a moment, or rephrase it.";
      setMessages(prev => [...prev.map(m => ({ ...m, _isNew: false })), {
        role: "assistant",
        content: friendly,
        error: raw,
        _isNew: true,
      }]);
    } finally {
      setLoading(false);
      // Refresh usage after each question
      try {
        const usageRes = await fetch(`${API_URL}/ai/usage`, { headers: { Authorization: `Bearer ${idToken}` } });
        if (usageRes.ok) setAiUsage(await usageRes.json());
      } catch {}
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    askQuestion(input);
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "yesterday";
    return `${days}d ago`;
  };

  // Export a Q&A exchange (question + answer + chart + table) to PDF
  const exportToPDF = async (msgIndex) => {
    const msg = messages[msgIndex];
    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 12;

      // Header
      pdf.setFontSize(16);
      pdf.setTextColor(99, 102, 241);
      pdf.text("Boomerang AI — Insights Report", margin, 18);
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(new Date().toLocaleString(), margin, 24);

      let cursorY = 32;

      // Question (find the preceding user message)
      const question = msgIndex > 0 && messages[msgIndex - 1]?.role === "user"
        ? messages[msgIndex - 1].content : "";
      if (question) {
        pdf.setFontSize(11);
        pdf.setTextColor(40, 40, 40);
        pdf.setFont(undefined, "bold");
        const qLines = pdf.splitTextToSize(`Q: ${question}`, pageWidth - margin * 2);
        pdf.text(qLines, margin, cursorY);
        cursorY += qLines.length * 6 + 2;
      }

      // Answer text
      if (msg.content) {
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        pdf.setFont(undefined, "normal");
        const aLines = pdf.splitTextToSize(msg.content, pageWidth - margin * 2);
        pdf.text(aLines, margin, cursorY);
        cursorY += aLines.length * 5 + 4;
      }

      // Chart (as image, if present) — fast SVG serialization (not html2canvas).
      const chartEl = document.querySelector(`#msg-block-${msgIndex} .recharts-wrapper`);
      if (chartEl) {
        try {
          const png = await svgChartToPng(chartEl, 2);
          if (png) {
            const imgW = pageWidth - margin * 2;
            const imgH = (png.h * imgW) / png.w;
            if (cursorY + imgH > pdf.internal.pageSize.getHeight() - margin) {
              pdf.addPage();
              cursorY = margin;
            }
            pdf.addImage(png.dataUrl, "PNG", margin, cursorY, imgW, imgH);
            cursorY += imgH + 4;
            // Legend (the HTML legend isn't part of the svg) — draw series swatches + names.
            const yk = (msg.chart && msg.chart.y_keys) || [];
            if (yk.length) {
              const pal = (brandPalette && brandPalette.length) ? brandPalette : CHART_COLORS;
              pdf.setFontSize(8); pdf.setFont(undefined, "normal");
              let lx = margin;
              yk.forEach((k, i) => {
                const [r, g, b] = _hexToRgb(pal[i % pal.length]);
                const label = String(k).replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
                const tw = pdf.getTextWidth(label);
                if (lx + 4 + tw > pageWidth - margin) { lx = margin; cursorY += 5; }
                pdf.setFillColor(r, g, b);
                pdf.rect(lx, cursorY - 2.6, 3, 3, "F");
                pdf.setTextColor(60, 60, 60);
                pdf.text(label, lx + 4.5, cursorY);
                lx += 4.5 + tw + 7;
              });
              cursorY += 8;
            }
          }
        } catch (e) { /* skip chart if capture fails */ }
      }

      // Data table (full, paginated, selectable text)
      if (msg.rawData && msg.rawData.length > 0) {
        const columns = Object.keys(msg.rawData[0]);
        const head = [columns.map(c => c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()))];
        const body = msg.rawData.map(row => columns.map(c => formatCell(c, row[c], msg.columnMeta && msg.columnMeta[c])));
        autoTable(pdf, {
          head, body,
          startY: cursorY,
          margin: { left: margin, right: margin },
          styles: { fontSize: 7, cellPadding: 1.5 },
          headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: "bold" },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          // Color delta/signed cells red (loss) / green (gain) to match the on-screen table
          didParseCell: (d) => {
            if (d.section !== "body") return;
            const col = columns[d.column.index];
            const raw = msg.rawData[d.row.index]?.[col];
            const c = cellTextColor(col, raw);
            if (c === "#dc2626") { d.cell.styles.textColor = [220, 38, 38]; d.cell.styles.fontStyle = "bold"; }
            else if (c === "#16a34a") { d.cell.styles.textColor = [22, 163, 74]; d.cell.styles.fontStyle = "bold"; }
          },
        });
      }

      pdf.save(`boomerang-report-${Date.now()}.pdf`);
    } catch (e) {
      console.error("PDF export failed", e);
      notify("error", "PDF export failed", "We couldn't generate the PDF. Please try again.");
    }
  };

  // Export the report's underlying result rows (msg.rawData — the exact data the
  // table/chart/answer were built from) as an analysis-ready CSV. Pure client-side:
  // the rows are already in the browser, so there's no extra query, Athena, or cost.
  // Raw values are written (not display-formatted) so the file re-imports cleanly.
  const exportToCSV = (msgIndex) => {
    const msg = messages[msgIndex];
    if (!msg.rawData || msg.rawData.length === 0) {
      notify("warning", "Nothing to export", "This answer doesn't have a data table to export.");
      return;
    }
    try {
      const columns = Object.keys(msg.rawData[0]);
      // RFC-4180 escaping: quote fields containing comma, quote, or newline; double inner quotes.
      const esc = (v) => {
        if (v === null || v === undefined) return "";
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [columns.join(",")];
      for (const row of msg.rawData) lines.push(columns.map((c) => esc(row[c])).join(","));
      // Prepend a UTF-8 BOM so Excel opens accented/unicode text correctly.
      const csv = "\uFEFF" + lines.join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `boomerang-report-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV export failed", e);
      notify("error", "CSV export failed", "We couldn't generate the CSV. Please try again.");
    }
  };

  // Pin a chat answer's chart onto the Auto-Insights board (R6) — converts the ai/ask chart
  // into the board's chart_spec shape and POSTs it as a custom visual. It then renders on the
  // board and is included in the PowerPoint export. Formats come from the authoritative
  // column_meta so money/percent labels match the rest of the board.
  const addToBoard = async (msgIndex) => {
    const msg = messages[msgIndex];
    const c = msg && msg.chart;
    // Pin the EXACT data that was plotted (`chart.data`) so the board card matches the chat chart.
    // `_build_smart_chart` may aggregate/bucket the raw result (e.g. 92 daily rows → 3 monthly
    // bars); pinning `rawData` instead rendered the raw un-aggregated series on the board (a dense
    // daily chart that looked nothing like the answer). Fall back to `rawData` ONLY when there's no
    // plotted chart data (e.g. a table/record answer being pinned).
    const rows = (c && Array.isArray(c.data) && c.data.length) ? c.data
      : (msg && msg.rawData && msg.rawData.length ? msg.rawData : []);
    if (!c || rows.length === 0) {
      notify("warning", "Nothing to add", "This answer doesn't include a chart to pin to the board.");
      return;
    }
    // Guard against rapid double-clicks / re-adds: the ref check is synchronous (state updates
    // are async, so it alone can't stop back-to-back clicks), and once added the button locks.
    if (addInFlightRef.current.has(msgIndex) || (boardAdds[msgIndex] && boardAdds[msgIndex].state === "added")) return;
    addInFlightRef.current.add(msgIndex);
    setBoardAdds((m) => ({ ...m, [msgIndex]: { state: "adding" } }));
    const _clearAdd = () => setBoardAdds((m) => { const n = { ...m }; delete n[msgIndex]; return n; });
    const typeMap = { pie: "donut", doughnut: "donut", donut: "donut", area: "line", line: "line", bar: "bar" };
    const question = msgIndex > 0 && messages[msgIndex - 1]?.role === "user" ? messages[msgIndex - 1].content : "";
    const firstRow = rows[0] || {};
    const series = (c.y_keys && c.y_keys.length) ? c.y_keys
      : Object.keys(firstRow).filter((k) => k !== c.x_key).slice(0, 3);
    const formats = {};
    series.forEach((k) => { formats[k] = (msg.columnMeta && msg.columnMeta[k] && msg.columnMeta[k].format) || "number"; });
    // Preserve a combined (dual-axis #8 / stacked #4) chart's render intent when pinning, so the
    // board + PPTX export match what's shown live (otherwise it collapses to plain grouped bars).
    const isCombo = c.type === "combo" || c.type === "stacked_bar";
    const specType = isCombo ? c.type : (typeMap[String(c.type)] || "bar");
    const chartSpec = {
      type: specType, x: c.x_key || "", series,
      formats: (c.formats && Object.keys(c.formats).length) ? c.formats : formats,
      order: {},
    };
    if (isCombo) {
      if (c.series_types) chartSpec.series_types = c.series_types;
      if (c.axis) chartSpec.axis = c.axis;
      if (c.stack) chartSpec.stack = c.stack;
    }
    // REAL BUG, reported live: a chart with GROUP-BY-COMPANY BANDS (the "25-34"/"35-44"/...
    // labeled headers this component's own RenderChart draws via group_key/CompanyBands below)
    // rendered those bands here in Analyze, but lost them entirely once pinned to the board —
    // this key was never included in the pin payload at all, even though `c.group_key` and the
    // group column on each pinned row (already present in `rows`, unchanged) both survive.
    if (c.group_key) chartSpec.group_key = c.group_key;
    const payload = {
      title: String(c.title || question || "Custom view").slice(0, 160),
      chart_spec: chartSpec,
      result_set: rows.slice(0, 300),
      narrative: String(msg.content || "").slice(0, 600),
      sql: msg.sql || "",
      columns: Object.keys(firstRow),
    };
    try {
      const res = await authFetch(`${API_URL}/ai/board/custom`,
        { method: "POST", headers: { "Content-Type": "application/json", "X-Project-Id": activeProjectId }, body: JSON.stringify(payload) },
        idToken);
      if (res.ok) { setBoardRefresh((n) => n + 1); setBoardAdds((m) => ({ ...m, [msgIndex]: { state: "added" } })); notify("success", "Added to your board", "This chart is now on your Auto-Insights board and will be included in the PowerPoint export."); }
      else if (res.status === 409) { _clearAdd(); notify("warning", "Board is full", "You've reached the limit of pinned charts for this data. Remove one to add another."); }
      else { _clearAdd(); notify("error", "Couldn't add to board", "Something went wrong pinning this chart. Please try again."); }
    } catch (e) { _clearAdd(); notify("error", "Couldn't add to board", "Something went wrong pinning this chart. Please try again."); }
    finally { addInFlightRef.current.delete(msgIndex); }
  };

  // AutoDeck reports its persisted pinned charts here → map signature to the custom-visual id.
  const handlePinnedChange = useCallback((customs) => {
    const map = {};
    (customs || []).forEach((v) => { map[pinnedVisualSignature(v)] = v.id; });
    setPinnedSigs(map);
  }, []);

  // Derive each chat answer's "✓ Added" state from the REAL board (match by signature), so it's
  // accurate across reloads and reflects task-26 carry-forward/drop. In-flight "adding" is kept.
  useEffect(() => {
    setBoardAdds((prev) => {
      const next = {};
      messages.forEach((m, i) => {
        if (m.role !== "assistant" || !m.chart) return;
        if (prev[i] && prev[i].state === "adding") { next[i] = prev[i]; return; }
        const q = i > 0 && messages[i - 1] && messages[i - 1].role === "user" ? messages[i - 1].content : "";
        const id = pinnedSigs[chatChartSignature(m.chart, q, m.rawData)];
        if (id) next[i] = { state: "added", customId: id };
      });
      return next;
    });
  }, [pinnedSigs, messages]);

  // Reconcile the board (→ the "✓ Added" badges) when this tab regains focus/visibility, so a
  // change made elsewhere (e.g. removing the card on the Dashboard tab in another window) is
  // reflected here without a manual reload. In-app tab switches already remount + refetch.
  useEffect(() => {
    if (!autoDeckOn) return undefined;
    const onFocus = () => { if (document.visibilityState === "visible") setBoardRefresh((n) => n + 1); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [autoDeckOn]);

  // Remove a pinned chart from the board (the "✓ Added" toggle). Refetches so state re-derives.
  const removeFromBoard = async (msgIndex, customId) => {
    if (!customId) return;
    try {
      const res = await authFetch(`${API_URL}/ai/board/custom/${encodeURIComponent(customId)}`,
        { method: "DELETE", headers: { "X-Project-Id": activeProjectId } }, idToken);
      // Idempotent: the DELETE endpoint returns 200 even if the id was already gone (e.g. removed
      // from the Dashboard tab). Treat ANY completed response as "it's off the board" and clear the
      // local state + refetch, so the "✓ Added" toggle can never get stuck out of sync. Only a
      // network/transport failure (no response) is a real error.
      setBoardAdds((m) => { const n = { ...m }; delete n[msgIndex]; return n; });
      setBoardRefresh((n) => n + 1);
      if (res.ok) notify("success", "Removed from board", "This chart is no longer on your Auto-Insights board.");
      else notify("warning", "Removed", "This chart wasn't on the board (it may have been removed elsewhere).");
    } catch (e) { notify("error", "Couldn't remove", "Network hiccup removing this chart. Please try again."); }
  };

  return (
    <div style={{ display: "flex", height: "calc(100vh - 80px)", gap: "0" }}>
      <ToastView toast={toast} onClose={() => setToast(null)} />
      <ConfirmDialog open={!!confirmState} {...(confirmState || {})} onCancel={() => setConfirmState(null)} />

      {/* Main chat area */}
      <div style={{ display: "flex", flexDirection: "column", flex: 1, padding: "20px", minWidth: 0 }}>
        
        {/* Compact header (option B) — ONE slim row: icon + title + Auto-Insights + "updated",
            with the project selector + Clear history on the right. Frees vertical space for the
            working area; the standalone descriptive subtitle was dropped (it duplicated the
            input placeholder). The Auto-Insights banner + the collapsible Insight Engine panel
            + the board action toolbar are all unchanged. */}
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
          <div style={{
            width: "26px", height: "26px", borderRadius: "8px",
            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
          }}>
            <Brain size={15} color="#fff" />
          </div>
          <h1 style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a", margin: 0 }}>AI Insights</h1>
          {/* Mode indicator (R21.3): only shown when the Auto-Deck flag is ON. */}
          {autoDeckOn && (
            <span title="Auto-Insights is on: an executive board is generated from your data"
              style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, color: "#4f46e5", background: "#eef2ff", border: "1px solid #e0e7ff" }}>
              <Sparkles size={11} /> Auto-Insights
            </span>
          )}
          {dataFreshness && (
            <span style={{ fontSize: "11px", color: "#10b981", fontWeight: 500, whiteSpace: "nowrap" }}>
              • updated {timeAgo(dataFreshness)}
            </span>
          )}
          <div style={{ flex: 1, minWidth: "8px" }} />
          <ProjectBar inline />
          {/* Chat-memory controls — only when there's a conversation to act on. */}
          {chatMeta.enabled && messages.length > 0 && (
            <button onClick={clearChatHistory}
              title="Permanently delete all of this project's saved conversations. This can't be undone."
              style={memoryBtnStyle}>
              <Trash2 size={12} /> Clear history
            </button>
          )}
        </div>

        {/* Data-updated banner — the project's data changed since this conversation
            started, so earlier answers may be stale. One subtle line + one action. */}
        {chatMeta.enabled && chatMeta.stale && messages.length > 0 && (
          <div style={{
            flexShrink: 0, display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px",
            padding: "9px 13px", borderRadius: "10px", background: "#fffbeb", border: "1px solid #fde68a",
          }}>
            <RefreshCw size={14} style={{ color: "#d97706", flexShrink: 0 }} />
            <span style={{ fontSize: "12.5px", color: "#92400e", flex: 1 }}>
              This project's data was updated since this conversation started — earlier answers may be out of date.
            </span>
            <button onClick={startNewChat}
              title="Start a new conversation that uses your just-updated data. Earlier answers stay in history but won't be mixed with the new data."
              style={{
              fontSize: "12px", fontWeight: 700, padding: "5px 12px", borderRadius: "8px",
              border: "1px solid #fcd34d", background: "#fff", color: "#92400e", cursor: "pointer", whiteSpace: "nowrap",
            }}>Start fresh with latest data</button>
          </div>
        )}

        {/* Anomaly Alerts Banner */}
        {alerts.length > 0 && (
          <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: "8px", marginBottom: "8px" }}>
            {alerts.map((alert) => {
              const severityColors = {
                high: { bg: "#fef2f2", border: "#fecaca", icon: "#dc2626", text: "#991b1b" },
                medium: { bg: "#fffbeb", border: "#fde68a", icon: "#d97706", text: "#92400e" },
                low: { bg: "#eff6ff", border: "#bfdbfe", icon: "#2563eb", text: "#1e40af" }
              };
              const colors = severityColors[alert.severity] || severityColors.medium;
              return (
                <div key={alert.id} style={{
                  display: "flex", alignItems: "flex-start", gap: "10px",
                  padding: "12px 14px", borderRadius: "10px",
                  background: colors.bg, border: `1px solid ${colors.border}`,
                  animation: "fadeIn 0.3s ease"
                }}>
                  <div style={{
                    width: "8px", height: "8px", borderRadius: "50%",
                    background: colors.icon, marginTop: "5px", flexShrink: 0,
                    animation: "pulse 2s infinite"
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ fontSize: "12px", fontWeight: 700, color: colors.text }}>{alert.title}</span>
                      <span style={{ fontSize: "9px", color: colors.icon, background: `${colors.icon}15`, padding: "1px 6px", borderRadius: "8px", fontWeight: 600 }}>
                        {alert.severity.toUpperCase()}
                      </span>
                    </div>
                    <p style={{ fontSize: "11px", color: "#475569", margin: "3px 0 0", lineHeight: 1.4 }}>{alert.message}</p>
                    {alert.investigation_query && (
                      <button onClick={() => askQuestion(alert.investigation_query)} style={{
                        fontSize: "10px", padding: "4px 10px", borderRadius: "12px", marginTop: "6px",
                        border: `1px solid ${colors.border}`, background: "#fff",
                        color: colors.text, cursor: "pointer", fontWeight: 600
                      }}>
                        Investigate →
                      </button>
                    )}
                  </div>
                  <button onClick={() => dismissAlert(alert.id)} style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "#94a3b8", padding: "2px", flexShrink: 0
                  }}>
                    <XIcon size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Messages area */}
        <div style={{
          flex: 1, overflowY: "auto", padding: "16px 0",
          display: "flex", flexDirection: "column", gap: "16px"
        }}>
          {/* Auto-Deck (flag ON) leads AI Insights — the executive board generated from this
              project's data. Shown whenever the flag is on (NOT only on an empty chat), so
              returning users with conversation history still see the board; the typed chat
              continues below it. */}
          {autoDeckOn && (
            <AutoDeck projectId={activeProjectId} onDrill={(q) => askQuestion(q)} refreshSignal={boardRefresh} onThemeChange={setBrandPalette} brandColorsOn={brandColorsOn} onPinnedChange={handlePinnedChange} />
          )}

          {!autoDeckOn && messages.length === 0 && !loading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: "14px", textAlign: "center", padding: "24px" }}>
              <div style={{ width: "64px", height: "64px", borderRadius: "18px", background: "linear-gradient(135deg, #eef2ff, #e0e7ff)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 28px rgba(99,102,241,0.18)" }}>
                <Sparkles size={28} color="#6366f1" />
              </div>
              <h2 style={{ fontSize: "20px", fontWeight: 700, color: "#0f172a", margin: 0, letterSpacing: "-0.3px" }}>What would you like to know?</h2>
              <p style={{ fontSize: "13.5px", color: "#64748b", margin: 0, maxWidth: "380px", lineHeight: 1.55 }}>
                Ask a question about your data in plain English — or pick a suggested insight from the panel on the right. Every answer comes with a chart and the exact records behind it.
              </p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} id={`msg-block-${i}`} style={{ display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start" }}>
              {/* Message bubble */}
              <div style={{
                maxWidth: "85%", padding: "12px 16px", borderRadius: "12px",
                background: msg.role === "user" ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : "#f8fafc",
                color: msg.role === "user" ? "#fff" : "#1e293b",
                border: msg.role === "user" ? "none" : "1px solid #e2e8f0",
                fontSize: "14px", lineHeight: 1.6
              }}>
                {msg.role === "assistant" && i === messages.length - 1 && msg._isNew ? (
                  <TypedText text={msg.content} speed={10} />
                ) : msg.role === "assistant" ? (
                  <RichText text={msg.content} />
                ) : (
                  msg.content
                )}
              </div>

              {/* KPI Card — for single-value metrics */}
              {msg.role === "assistant" && msg.rawData && msg.rawData.length <= 2 && msg.rawData.length > 0 && !msg.chart && (() => {
                const keys = Object.keys(msg.rawData[0]);
                const meta = msg.columnMeta || null;
                // Prefer the AUTHORITATIVE backend meta to choose the headline metric (a real
                // currency/percent/ratio/measure — NEVER an id/date). Fall back to the
                // conservative name/value heuristics only when meta is absent (old messages).
                let mainKey = _pickHeadlineMetric(meta, keys);
                let numericKeys;
                if (mainKey) {
                  numericKeys = keys.filter(k => meta[k] && _METRIC_ROLES.includes(meta[k].role));
                } else if (!meta) {
                  numericKeys = keys.filter(k => {
                    const v = msg.rawData[0][k];
                    return v && !isNaN(parseFloat(String(v).replace(/,/g, '')));
                  }).filter(k => !_isIdLikeColumn(k) && !_looksLikeDate(msg.rawData[0][k]));
                  mainKey = numericKeys.find(_isCurrencyColumn) || numericKeys[0];
                } else {
                  numericKeys = []; // meta present but no metric column → no headline card
                }
                if (!mainKey) return null;

                const rows = msg.rawData;
                // Label = a descriptive column, never an ID/metric column.
                const labelKey = meta
                  ? keys.find(k => meta[k] && meta[k].role === "categorical")
                  : keys.find(k => !numericKeys.includes(k) && !_isIdLikeColumn(k));

                return (
                  <div style={{ maxWidth: "85%", marginTop: "12px", width: "100%", display: "flex", gap: "12px", flexWrap: "wrap" }}>
                    {rows.map((row, ri) => {
                      // Format via the shared formatCell using the column's meta — $ ONLY for
                      // genuine currency columns; IDs/counts/dates stay plain.
                      const label = labelKey ? row[labelKey] : mainKey.replace(/_/g, ' ');
                      const formattedValue = formatCell(mainKey, row[mainKey], meta && meta[mainKey]);

                      return (
                        <div key={ri} style={{
                          flex: "1 1 180px", padding: "20px 24px", borderRadius: "14px",
                          background: "#fff", border: "1px solid #e0e7ff",
                          boxShadow: "0 2px 8px rgba(99,102,241,0.06)",
                          display: "flex", flexDirection: "column", gap: "6px"
                        }}>
                          <span style={{ fontSize: "11px", fontWeight: 600, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {label}
                          </span>
                          <span style={{ fontSize: "32px", fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                            {formattedValue}
                          </span>
                          <span style={{ fontSize: "10px", color: "#94a3b8" }}>
                            {mainKey.replace(/_/g, ' ')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Chart */}
              {msg.chart && msg.chart.data?.length > 0 && (
                <div style={{ maxWidth: "85%", marginTop: "12px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", width: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <p style={{ fontSize: "12px", fontWeight: 600, color: "#475569", margin: 0 }}>
                      <BarChart3 size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: "6px" }} />
                      {msg.chart.title || "Chart"}
                    </p>
                    <div style={{ display: "flex", gap: "6px" }}>
                      {autoDeckOn && (() => {
                        const st = boardAdds[i];                 // {state:"adding"|"added", customId?}
                        const done = st && st.state === "added";
                        const busy = st && st.state === "adding";
                        if (done) {
                          // Accurate "on board" state (derived from the persisted board) + Remove toggle.
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                              <span style={{ fontSize: "10px", padding: "4px 8px", borderRadius: "12px", border: "1px solid #16a34a", background: "#dcfce7", color: "#166534", fontWeight: 700 }}>✓ Added</span>
                              <button onClick={() => removeFromBoard(i, st.customId)} disabled={!st.customId}
                                title="Remove this chart from your Auto-Insights board"
                                style={{ fontSize: "10px", padding: "4px 8px", borderRadius: "12px", border: "1px solid #fecaca", background: "#fff", color: "#b91c1c", cursor: st.customId ? "pointer" : "default", fontWeight: 600 }}>
                                Remove
                              </button>
                            </span>
                          );
                        }
                        return (
                        <button onClick={() => addToBoard(i)} disabled={busy} style={{
                          fontSize: "10px", padding: "4px 10px", borderRadius: "12px",
                          border: "1px solid #4f46e5",
                          background: busy ? "#c7d2fe" : "#4f46e5",
                          color: busy ? "#3730a3" : "#fff",
                          cursor: busy ? "default" : "pointer", fontWeight: 700, display: "flex", alignItems: "center", gap: "4px"
                        }}
                        title="Pin this chart to your Auto-Insights board (also included in the PowerPoint export)">
                          {busy ? "Adding…" : "＋ Add to board"}
                        </button>
                        );
                      })()}
                      <button onClick={() => exportToPDF(i)} style={{
                        fontSize: "10px", padding: "4px 10px", borderRadius: "12px",
                        border: "1px solid #e0e7ff", background: "#eef2ff", color: "#4f46e5",
                        cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px"
                      }}
                      title="Export this insight as PDF">
                        📄 Export PDF
                      </button>
                      {msg.rawData && msg.rawData.length > 0 && (
                        <button onClick={() => exportToCSV(i)} style={{
                          fontSize: "10px", padding: "4px 10px", borderRadius: "12px",
                          border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534",
                          cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px"
                        }}
                        title="Export the report data as CSV">
                          ⬇ Export CSV
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ width: "100%", height: "260px" }}>
                    <RenderChart chart={msg.chart} palette={brandPalette} />
                  </div>
                </div>
              )}

              {/* Inline data table — shown when user asked for table/report format */}
              {msg.role === "assistant" && msg.displayPreference === "table" && msg.rawData && msg.rawData.length > 0 && (
                <div style={{ maxWidth: "90%", marginTop: "12px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", width: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <p style={{ fontSize: "12px", fontWeight: 600, color: "#475569", margin: 0 }}>
                      <Table2 size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: "6px" }} />
                      Report — {msg.rawData.length} rows
                    </p>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button onClick={() => exportToPDF(i)} style={{
                        fontSize: "10px", padding: "4px 10px", borderRadius: "12px",
                        border: "1px solid #e0e7ff", background: "#eef2ff", color: "#4f46e5",
                        cursor: "pointer", fontWeight: 600
                      }}
                      title="Export this table as PDF">
                        📄 Export PDF
                      </button>
                      <button onClick={() => exportToCSV(i)} style={{
                        fontSize: "10px", padding: "4px 10px", borderRadius: "12px",
                        border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534",
                        cursor: "pointer", fontWeight: 600
                      }}
                      title="Export the report data as CSV">
                        ⬇ Export CSV
                      </button>
                    </div>
                  </div>
                  <DataTable rows={msg.rawData} columnMeta={msg.columnMeta} />
                </div>
              )}

              {/* Follow-up questions */}
              {msg.role === "assistant" && msg.followUps && msg.followUps.length > 0 && (
                <div style={{ display: "flex", gap: "6px", marginTop: "10px", flexWrap: "wrap", maxWidth: "85%" }}>
                  {msg.followUps.map((q, fi) => (
                    <button key={fi} onClick={() => askQuestion(q)} style={{
                      fontSize: "11px", padding: "6px 12px", borderRadius: "16px",
                      border: "1px solid #c7d2fe", background: "#eef2ff",
                      color: "#4f46e5", cursor: "pointer", transition: "all 0.15s"
                    }}
                    onMouseEnter={e => { e.target.style.background = "#6366f1"; e.target.style.color = "#fff"; }}
                    onMouseLeave={e => { e.target.style.background = "#eef2ff"; e.target.style.color = "#4f46e5"; }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Traceable Truth — Source verification badges */}
              {msg.role === "assistant" && (msg.sql || msg.rawData) && (
                <div style={{ display: "flex", gap: "6px", marginTop: "10px", alignItems: "center", flexWrap: "wrap" }}>
                  {msg.rawData && msg.rawData.length > 0 && (
                    <button onClick={() => setShowData(showData === `data-${i}` ? null : `data-${i}`)} style={{
                      fontSize: "11px", padding: "6px 12px", borderRadius: "16px",
                      border: "1px solid #bbf7d0", 
                      background: showData === `data-${i}` ? "#16a34a" : "#f0fdf4",
                      color: showData === `data-${i}` ? "#fff" : "#166534", 
                      cursor: "pointer", display: "flex", alignItems: "center", gap: "5px",
                      fontWeight: 600, transition: "all 0.15s",
                      boxShadow: showData === `data-${i}` ? "0 2px 8px rgba(22,163,74,0.3)" : "none"
                    }}>
                      <Eye size={11} /> View {msg.rawData.length} Records
                      <CheckCircle2 size={10} />
                    </button>
                  )}
                  {msg.sql && (
                    <button onClick={() => setShowData(showData === `sql-${i}` ? null : `sql-${i}`)} style={{
                      fontSize: "11px", padding: "5px 10px", borderRadius: "14px",
                      border: "1px solid #e2e8f0", 
                      background: showData === `sql-${i}` ? "#1e293b" : "#f8fafc",
                      color: showData === `sql-${i}` ? "#e2e8f0" : "#64748b", 
                      cursor: "pointer", display: "flex", alignItems: "center", gap: "4px",
                      transition: "all 0.15s"
                    }}>
                      <Database size={10} /> Query
                    </button>
                  )}
                  {msg.rawData && msg.rawData.length > 0 && (
                    <span style={{ fontSize: "9px", color: "#16a34a", display: "flex", alignItems: "center", gap: "3px" }}>
                      <Shield size={9} /> Verified from source
                    </span>
                  )}
                  <button
                    onClick={() => {
                      const q = (messages[i - 1] && messages[i - 1].role === "user") ? messages[i - 1].content : "";
                      fetch(`${API_URL}/ai/feedback`, {
                        method: "POST",
                        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
                        body: JSON.stringify({ question: q, sql: msg.sql || "", helpful: false }),
                      }).catch(() => {});
                      setFlagged((f) => ({ ...f, [i]: true }));
                    }}
                    disabled={!!flagged[i]}
                    title="Report this answer as unhelpful"
                    style={{
                      fontSize: "11px", padding: "5px 10px", borderRadius: "14px",
                      border: "1px solid #e2e8f0",
                      background: flagged[i] ? "#f0fdf4" : "#f8fafc",
                      color: flagged[i] ? "#16a34a" : "#94a3b8",
                      cursor: flagged[i] ? "default" : "pointer",
                      display: "flex", alignItems: "center", gap: "4px",
                    }}
                  >
                    {flagged[i] ? <><CheckCircle2 size={11} /> Thanks</> : <><ThumbsDown size={11} /> Not helpful</>}
                  </button>
                </div>
              )}

              {/* SQL panel */}
              {showData === `sql-${i}` && msg.sql && (
                <div style={{ maxWidth: "85%", marginTop: "8px", background: "#1e293b", borderRadius: "8px", padding: "12px", width: "100%", overflow: "auto" }}>
                  <pre style={{ fontSize: "11px", color: "#e2e8f0", margin: 0, whiteSpace: "pre-wrap", fontFamily: "monospace" }}>{msg.sql}</pre>
                </div>
              )}

              {/* Data inspection panel — Traceable Truth */}
              {showData === `data-${i}` && msg.rawData && msg.rawData.length > 0 && (
                <div style={{ 
                  maxWidth: "90%", marginTop: "10px", background: "#fff", 
                  border: "1px solid #bbf7d0", borderRadius: "12px", 
                  width: "100%", overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(22,163,74,0.08)"
                }}>
                  {/* Panel header */}
                  <div style={{ 
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", background: "#f0fdf4", borderBottom: "1px solid #bbf7d0"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Shield size={13} color="#16a34a" />
                      <span style={{ fontSize: "11px", fontWeight: 700, color: "#166534" }}>
                        Source Records — {msg.rawData.length} verified rows
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button onClick={() => exportToCSV(i)} style={{
                        fontSize: "10px", padding: "3px 9px", borderRadius: "12px",
                        border: "1px solid #bbf7d0", background: "#fff", color: "#166534",
                        cursor: "pointer", fontWeight: 600
                      }}
                      title="Download all source records as CSV">
                        ⬇ Export CSV
                      </button>
                      <button onClick={() => setShowData(null)} style={{
                        background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: "2px"
                      }}>
                        <XIcon size={14} />
                      </button>
                    </div>
                  </div>
                  {/* Table */}
                  <div style={{ overflow: "auto", maxHeight: "320px", padding: "4px" }}>
                    <table style={{ width: "100%", fontSize: "11px", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ padding: "7px 8px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", textAlign: "center", fontWeight: 700, color: "#6366f1", whiteSpace: "nowrap", width: "32px" }}>#</th>
                          {Object.keys(msg.rawData[0]).map(key => (
                            <th key={key} style={{ padding: "7px 8px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", textAlign: "left", fontWeight: 600, color: "#475569", whiteSpace: "nowrap" }}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {msg.rawData.slice(0, 25).map((row, ri) => (
                          <tr key={ri} style={{ background: ri % 2 === 0 ? "#fff" : "#fafbfc" }}>
                            <td style={{ padding: "5px 8px", borderBottom: "1px solid #f1f5f9", color: "#94a3b8", textAlign: "center", fontSize: "10px" }}>{ri + 1}</td>
                            {Object.keys(row).map((key, ci) => { const _m = msg.columnMeta && msg.columnMeta[key]; return (
                              <td key={ci} style={{ padding: "5px 8px", borderBottom: "1px solid #f1f5f9", color: cellTextColor(key, row[key]) || "#334155", fontWeight: cellTextColor(key, row[key]) ? 600 : 400, whiteSpace: "nowrap" }}>{formatCell(key, row[key], _m)}</td>
                            ); })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {msg.rawData.length > 25 && (
                    <div style={{ padding: "8px 14px", background: "#f0fdf4", borderTop: "1px solid #dcfce7", textAlign: "center" }}>
                      <span style={{ fontSize: "10px", color: "#166534" }}>Showing 25 of {msg.rawData.length} source records</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Loading indicator */}
          {loading && <ThinkingIndicator />}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area — real UX suggestion: "does it always have to present the prompt at the
            very end of the prompt history?" This panel's own layout assumes a fixed
            `height: calc(100vh - 80px)` so the inner Messages div scrolls internally and this
            form stays put below it — but that assumed height doesn't account for every possible
            extra banner/notice above (data-freshness pill, chat-memory notice, alert cards), and
            `.layout-content` (the page's OUTER wrapper, MainLayout.css) is ALSO independently
            scrollable. If those two disagree on the available height, the OUTER scroll can take
            over and scroll this ENTIRE panel — header, messages, AND the input — together,
            which is exactly "the input is buried at the bottom of a long conversation." A
            `position: sticky; bottom: 0` fix here pins the input to the bottom of WHICHEVER
            scroll container ends up active (inner or outer) with no layout restructuring risk —
            it's a no-op when nothing scrolls, and self-corrects when something does. */}
        <form onSubmit={handleSubmit} style={{
          display: "flex", gap: "10px", padding: "14px 0 6px", borderTop: "1px solid #eef1f6", flexShrink: 0,
          position: "sticky", bottom: 0, background: "#fff", zIndex: 5,
          alignItems: "flex-end"
        }}>
          {/* Multi-line prompt (roadmap: taller input + scrollbar). A <textarea> replaces the
              old single-line <input> so a long question is fully visible across multiple lines;
              it starts ~2x taller (minHeight 92px vs the old ~46px single row) and scrolls
              vertically (overflowY:auto) once content exceeds the box, and the user can drag to
              resize taller. A textarea does NOT submit on Enter natively (unlike an input in a
              form), so onKeyDown restores Enter-to-Ask while keeping Shift+Enter for a newline —
              preserving the exact submit affordance the input had. */}
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
            }}
            placeholder="Ask a question about your data... (Shift+Enter for a new line)"
            disabled={loading}
            rows={3}
            style={{
              flex: 1, padding: "13px 16px", fontSize: "14px",
              border: "1px solid #e2e8f0", borderRadius: "12px",
              outline: "none", background: "#fff",
              boxShadow: "0 1px 2px rgba(16,24,40,.04)",
              transition: "border-color 0.15s, box-shadow 0.15s",
              minHeight: "92px", maxHeight: "220px", overflowY: "auto",
              resize: "vertical", fontFamily: "inherit", lineHeight: 1.5
            }}
            onFocus={e => { e.target.style.borderColor = "#6366f1"; e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,.12)"; }}
            onBlur={e => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "0 1px 2px rgba(16,24,40,.04)"; }}
          />
          <button type="submit" disabled={loading || !input.trim()} style={{
            padding: "13px 22px", borderRadius: "12px", border: "none",
            background: loading || !input.trim() ? "#e2e8f0" : "linear-gradient(135deg, #6366f1, #7c3aed)",
            color: loading || !input.trim() ? "#94a3b8" : "#fff",
            cursor: loading || !input.trim() ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 600,
            boxShadow: loading || !input.trim() ? "none" : "0 6px 16px rgba(99,102,241,.28)"
          }}>
            <Send size={16} /> Ask
          </button>
        </form>
        {/* Chat-memory hint — follow-ups build on the current conversation, so prior questions
            can shape a new answer/chart. Shown only when a conversation exists; offers a one-click
            clear so the next question is evaluated independently. */}
        {chatMeta.enabled && messages.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", padding: "4px 0 0", flexShrink: 0 }}>
            <Info size={11} style={{ color: "#94a3b8", flexShrink: 0 }} />
            <span style={{ fontSize: "11px", color: "#94a3b8", textAlign: "center", lineHeight: 1.4 }}>
              Follow-ups build on this conversation, so earlier questions can shape a new answer or chart. For an independent result,{" "}
              <button type="button" onClick={clearChatHistory}
                style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "#6366f1", cursor: "pointer", textDecoration: "underline" }}>
                clear history
              </button>.
            </span>
          </div>
        )}
        <div style={{ textAlign: "center", padding: "2px 0 0" }}>
          <span style={{ fontSize: "9px", color: "#94a3b8" }}>Powered by Claude Sonnet 4.5 · Amazon Bedrock · Amazon Athena</span>
        </div>
      </div>

      {/* Right sidebar — Persistent Suggested Insights (collapsible) */}
      {showInsights ? (
      <div style={{
        width: "280px", flexShrink: 0, borderLeft: "1px solid #e0e7ff",
        padding: "24px 16px", overflowY: "auto",
        background: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
        display: "flex", flexDirection: "column", gap: "16px"
      }}>
        {/* Panel header */}
        <div style={{ 
          display: "flex", alignItems: "center", gap: "8px",
          padding: "10px 12px", background: "#fff", borderRadius: "10px",
          border: "1px solid #e0e7ff", boxShadow: "0 1px 3px rgba(99, 102, 241, 0.08)"
        }}>
          <div style={{
            width: "28px", height: "28px", borderRadius: "7px",
            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <Sparkles size={14} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: "12px", fontWeight: 700, color: "#1e293b", display: "block" }}>Insight Engine</span>
            {suggestedInsights === null ? (
              <span style={{ fontSize: "10px", color: "#6366f1", display: "inline-flex", alignItems: "center", gap: "4px", fontWeight: 600 }}>
                <RefreshCw size={9} className="animate-spin" /> Refreshing for this project…
              </span>
            ) : (
              <span style={{ fontSize: "10px", color: "#64748b" }}>Tailored to your data</span>
            )}
          </div>
          <button onClick={() => setShowInsights(false)} title="Hide panel" aria-label="Hide insights panel" style={{
            background: "none", border: "none", cursor: "pointer", color: "#94a3b8",
            padding: "4px", display: "flex", alignItems: "center", borderRadius: "6px"
          }}>
            <ChevronRight size={16} />
          </button>
        </div>

        {suggestedInsights === null ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} style={{ padding: "12px 13px", borderRadius: "10px", background: "#fff", border: "1px solid #e0e7ff", display: "flex", flexDirection: "column", gap: "7px" }}>
                <div className="skeleton" style={{ width: `${88 - i * 7}%`, height: "10px" }} />
                <div className="skeleton" style={{ width: `${60 - i * 5}%`, height: "10px" }} />
              </div>
            ))}
          </div>
        ) : suggestedInsights.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {suggestedInsights.map((q, i) => (
              <button key={i} onClick={() => askQuestion(q, i)} disabled={loading} style={{
                padding: "11px 13px", fontSize: "11.5px",
                color: activeInsight === i ? "#fff" : "#374151",
                background: activeInsight === i 
                  ? "linear-gradient(135deg, #6366f1, #8b5cf6)" 
                  : "#fff",
                border: activeInsight === i ? "1px solid #6366f1" : "1px solid #e0e7ff",
                borderRadius: "9px",
                cursor: loading ? "not-allowed" : "pointer", 
                textAlign: "left",
                transition: "all 0.2s", 
                lineHeight: 1.45, 
                opacity: loading ? 0.6 : 1,
                boxShadow: activeInsight === i 
                  ? "0 4px 12px rgba(99, 102, 241, 0.3)" 
                  : "0 1px 2px rgba(0,0,0,0.04)"
              }}
              onMouseEnter={e => { 
                if (!loading && activeInsight !== i) { 
                  e.target.style.borderColor = "#a5b4fc"; 
                  e.target.style.background = "#eef2ff";
                  e.target.style.transform = "translateX(2px)";
                }
              }}
              onMouseLeave={e => { 
                if (activeInsight !== i) {
                  e.target.style.borderColor = "#e0e7ff"; 
                  e.target.style.background = "#fff";
                  e.target.style.transform = "none";
                }
              }}
              >
                {q}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ padding: "16px", background: "#fff", borderRadius: "10px", border: "1px solid #e0e7ff", textAlign: "center" }}>
            <TrendingUp size={20} color="#94a3b8" style={{ marginBottom: "8px" }} />
            <p style={{ fontSize: "11px", color: "#64748b", margin: 0, lineHeight: 1.5 }}>Upload data files to unlock personalized insight suggestions.</p>
          </div>
        )}

        {/* Bottom info */}
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "10px" }}>
          <button onClick={runAnomalyDetection} disabled={loading} style={{
            width: "100%", padding: "10px 12px", fontSize: "11px", fontWeight: 600,
            border: "1px solid #fde68a", borderRadius: "8px",
            background: "linear-gradient(135deg, #fffbeb, #fef3c7)",
            color: "#92400e", cursor: loading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
            transition: "all 0.15s"
          }}>
            <span style={{ fontSize: "13px" }}>🔍</span> Scan for Anomalies
          </button>
          <div style={{ padding: "12px", background: "rgba(255,255,255,0.7)", borderRadius: "8px", border: "1px solid #e0e7ff" }}>
            <p style={{ fontSize: "10px", color: "#6366f1", margin: "0 0 4px", fontWeight: 600 }}>💡 How it works</p>
            <p style={{ fontSize: "10px", color: "#64748b", margin: 0, lineHeight: 1.5 }}>
              Questions are AI-generated from your data schemas. Anomaly scan checks for outliers and shifts.
            </p>
            <p style={{ fontSize: "10px", color: "#64748b", margin: "6px 0 0", lineHeight: 1.5 }}>
              Two things shape an answer and its chart: your <strong>conversation history</strong> (follow-ups build on
              earlier questions in this chat — use "Clear history" to ask independently) and your <strong>latest data</strong>
              (uploading or moving files can change the numbers). For the exact same question and data, the result is
              consistent every time.
            </p>
          </div>
          {aiUsage && (
            <div style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <span style={{ fontSize: "10px", fontWeight: 600, color: "#475569" }}>AI Credits</span>
                <span style={{ fontSize: "10px", color: "#64748b" }}>${aiUsage.usage_usd.toFixed(2)} / ${aiUsage.budget_usd.toFixed(0)}</span>
              </div>
              <div style={{ height: "4px", background: "#e2e8f0", borderRadius: "2px", overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: "2px",
                  width: `${Math.min((aiUsage.usage_usd / aiUsage.budget_usd) * 100, 100)}%`,
                  background: aiUsage.usage_usd / aiUsage.budget_usd > 0.9 ? "#dc2626" : aiUsage.usage_usd / aiUsage.budget_usd > 0.7 ? "#f59e0b" : "#10b981",
                  transition: "width 0.3s"
                }} />
              </div>
              <span style={{ fontSize: "9px", color: "#94a3b8", marginTop: "4px", display: "block" }}>
                Resets {aiUsage.resets_on}
              </span>
            </div>
          )}
        </div>
      </div>
      ) : (
        /* Collapsed — a labeled vertical "Insight Engine" tab on the right edge, so
           it's obvious what expands it (the bare icon wasn't clear). */
        <div style={{
          width: "40px", flexShrink: 0, display: "flex", justifyContent: "center", paddingTop: "12px",
          background: "transparent"
        }}>
          <button onClick={() => setShowInsights(true)} title="Show Insight Engine" aria-label="Show Insight Engine panel" style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: "9px",
            padding: "12px 7px", borderRadius: "10px", border: "1px solid #e2e8f0",
            background: "#fff", cursor: "pointer", color: "#6366f1",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)", transition: "border-color .15s, box-shadow .15s"
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#c7d2fe"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(99,102,241,0.15)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)"; }}>
            <Sparkles size={16} />
            <span style={{ writingMode: "vertical-rl", fontSize: "11.5px", fontWeight: 700, letterSpacing: ".4px", color: "#475569" }}>
              Insight Engine
            </span>
            <ChevronRight size={13} style={{ transform: "rotate(180deg)", color: "#94a3b8" }} />
          </button>
        </div>
      )}
    </div>
  );
}


// ─── AI Thinking Indicator ────────────────────────────────────────────────────

function ThinkingIndicator() {
  const phases = [
    "Understanding your question",
    "Generating the query",
    "Scanning your data",
    "Analyzing results",
    "Composing the answer",
  ];
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPhase(p => (p + 1) % phases.length), 1800);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 18px" }}>
      {/* Rotating ring with pulsing brain */}
      <div style={{ position: "relative", width: "40px", height: "40px", flexShrink: 0 }}>
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: "2.5px solid transparent",
          borderTopColor: "#6366f1", borderRightColor: "#8b5cf6",
          animation: "spin 0.9s linear infinite",
        }} />
        <div style={{
          position: "absolute", inset: "6px", borderRadius: "50%",
          background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "aiPulse 1.5s ease-in-out infinite",
        }}>
          <Brain size={16} color="#fff" />
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
        <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
          Boomerang AI is working
        </span>
        <span style={{ fontSize: "11.5px", color: "#6366f1", display: "flex", alignItems: "center", gap: "4px" }}>
          {phases[phase]}
          <span style={{ animation: "aiDots 1.4s infinite" }}>...</span>
        </span>
      </div>
    </div>
  );
}


// ─── Inline Data Table (for table-format reports + PDF export) ────────────────

function DataTable({ rows, maxRows = 100, columnMeta = null }) {
  if (!rows || rows.length === 0) return null;
  const columns = Object.keys(rows[0]);
  const displayRows = rows.slice(0, maxRows);

  return (
    <div style={{ overflow: "auto", maxHeight: "420px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
      <table style={{ width: "100%", fontSize: "11px", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ padding: "8px 10px", background: "#f1f5f9", borderBottom: "2px solid #e2e8f0", textAlign: "center", fontWeight: 700, color: "#6366f1", position: "sticky", top: 0, width: "36px" }}>#</th>
            {columns.map(col => (
              <th key={col} style={{ padding: "8px 10px", background: "#f1f5f9", borderBottom: "2px solid #e2e8f0", textAlign: "left", fontWeight: 700, color: "#334155", whiteSpace: "nowrap", position: "sticky", top: 0 }}>
                {col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? "#fff" : "#fafbfc" }}>
              <td style={{ padding: "6px 10px", borderBottom: "1px solid #f1f5f9", color: "#94a3b8", textAlign: "center", fontSize: "10px" }}>{ri + 1}</td>
              {columns.map(col => (
                <td key={col} style={{ padding: "6px 10px", borderBottom: "1px solid #f1f5f9", color: cellTextColor(col, row[col]) || "#334155", fontWeight: cellTextColor(col, row[col]) ? 600 : 400, whiteSpace: "nowrap" }}>{formatCell(col, row[col], columnMeta && columnMeta[col])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > maxRows && (
        <div style={{ padding: "8px 12px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", textAlign: "center", fontSize: "10px", color: "#64748b" }}>
          Showing {maxRows} of {rows.length} rows
        </div>
      )}
    </div>
  );
}


// ─── Typed Text (fake streaming effect) ──────────────────────────────────────

function TypedText({ text, speed = 12 }) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDisplayed("");
    setDone(false);
    if (!text) return;
    let i = 0;
    const interval = setInterval(() => {
      // Type 2-4 characters at a time for natural speed
      const chunk = Math.min(Math.floor(Math.random() * 3) + 2, text.length - i);
      i += chunk;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        setDone(true);
        clearInterval(interval);
      }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);

  return <>{displayed}{!done && <span style={{ opacity: 0.5 }}>▍</span>}</>;
}


// ─── Chart Renderer ──────────────────────────────────────────────────────────

function RenderChart({ chart, palette }) {
  if (!chart || !chart.data || chart.data.length === 0) return null;
  // Use the customer's brand palette when set (so chat charts match the board + PPTX), else
  // the default. `% length` guards a brand palette shorter than the number of series.
  const CC = (palette && palette.length) ? palette : CHART_COLORS;
  const col = (i) => CC[i % CC.length];

  const { type, data, x_key, y_keys, value_format } = chart;
  const isCurrency = value_format === "currency";

  // Auto-detect keys if not provided
  const keys = Object.keys(data[0] || {});
  const xKey = x_key || keys[0] || "";
  const yKeys = (y_keys && y_keys.length > 0) ? y_keys : keys.filter(k => k !== xKey).slice(0, 3);

  // Convert string numbers to actual numbers for charts
  const chartData = data.map(row => {
    const cleaned = { ...row };
    yKeys.forEach(k => {
      if (cleaned[k] !== undefined) {
        const num = parseFloat(cleaned[k]);
        if (!isNaN(num)) cleaned[k] = num;
      }
    });
    return cleaned;
  });

  // Time series: order points chronologically by the x-axis date, regardless of the
  // SQL's ORDER BY (it may have ordered by value). Only applies when x is a date.
  if (chartData.length > 1 && _looksLikeDate(chartData[0][xKey])) {
    chartData.sort((a, b) => {
      const da = Date.parse(String(a[xKey]).slice(0, 10));
      const db = Date.parse(String(b[xKey]).slice(0, 10));
      if (isNaN(da) || isNaN(db)) return 0;
      return da - db;
    });
  }

  // E (#77): GROUP-BY-COMPANY BANDS. The backend sets chart.group_key when the entity axis
  // (e.g. campaign_name) has a coarser secondary category (company/platform) and has PRE-SORTED
  // the rows into contiguous blocks per group. We draw a labeled band + faint divider over each
  // block on bar / combo / stacked charts so the reader sees which campaigns belong to which
  // company. Degrades gracefully: no group_key (or <2 groups) → nothing is drawn.
  const groupKey = (chart.group_key && chartData[0] && chartData[0][chart.group_key] !== undefined)
    ? chart.group_key : null;
  const bandGroups = [];
  if (groupKey) {
    chartData.forEach((row, i) => {
      const g = String(row[groupKey] ?? "");
      const last = bandGroups[bandGroups.length - 1];
      if (last && last.name === g) last.end = i;
      else bandGroups.push({ name: g, start: i, end: i });
    });
  }
  const hasBands = !!groupKey && bandGroups.length >= 2;
  // Categories are evenly spaced in a recharts categorical axis, so each group's pixel span is
  // derived from the plot area (offset from Customized) by index fraction — reliable without
  // reaching into recharts' internal scale objects.
  const CompanyBands = (props) => {
    const off = props.offset || {};
    const { left, top, width, height } = off;
    const n = chartData.length;
    if (!n || !width) return null;
    const band = width / n;
    const els = [];
    bandGroups.forEach((g, gi) => {
      const x0 = left + g.start * band;
      const x1 = left + (g.end + 1) * band;
      const cx = (x0 + x1) / 2;
      els.push(<text key={`bl-${gi}`} x={cx} y={top - 20} textAnchor="middle" fontSize={12} fontWeight={700} fill="#334155">{g.name || "Other"}</text>);
      els.push(<line key={`bu-${gi}`} x1={x0 + 5} y1={top - 12} x2={x1 - 5} y2={top - 12} stroke="#94a3b8" strokeWidth={2} />);
      if (gi > 0) els.push(<line key={`bd-${gi}`} x1={x0} y1={top - 6} x2={x0} y2={top + height} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="4 4" />);
    });
    return <g>{els}</g>;
  };
  const bandTopMargin = hasBands ? 46 : 16;

  // Value formatters — single-measure charts (bar/line/area/pie). Route through the shared
  // formatMetric keyed off the chart's `value_format`, so a single 'percent'/'ratio' measure
  // renders with the SAME adaptive precision as the combo and the tables (not a raw number).
  // currency/number keep their prior compact-axis / full-tooltip behavior exactly.
  const singleFmt = value_format || (isCurrency ? "currency" : "number");
  const fmtAxis = (v) => {
    if (typeof v !== "number") return v;
    return formatMetric(v, singleFmt, { compact: true });
  };
  const fmtTooltip = (v) => {
    if (typeof v !== "number") return v;
    // Tooltip shows the FULL value (no compacting): $1,234.50 / 69,466 / 0.32% / 3.5x.
    return formatMetric(v, singleFmt);
  };
  // X-axis / category labels: show friendly period labels ("Jan 2025", "Jan 6, 2025",
  // "Q1 2025") instead of raw "2025-01" / timestamps; non-period categories pass through.
  const fmtXTick = (v) => _friendlyPeriod(v);

  // Combined chart (dual-axis #8 / stacked #4, R26/24.5) — rendered from the deterministic
  // spec fields the backend attached (series_types/axis/stack/formats). Magnitude bars on the
  // left axis, a rate/percent line on the right; same-unit parts share a stack group.
  const seriesTypes = chart.series_types || null;
  if ((type === "combo" || type === "stacked_bar") && seriesTypes) {
    const axesMap = chart.axis || {};
    const stacks = chart.stack || {};
    const cfmts = chart.formats || {};
    // The combo renders through the SAME shared formatMetric as the tables (compact magnitudes for
    // the chart's axes/labels). A 'percent' fraction (CTR 0.0032) is ×100'd exactly once with
    // adaptive precision → "0.32%" (not "0%" and not "32%"); line values, axis ticks, labels and
    // the tooltip now all agree because they share this one formatter.
    const fmtBy = (f, v) => {
      if (typeof v !== "number") return v;
      return formatMetric(v, f || "number", { compact: true });
    };
    const primaryKeys = yKeys.filter((k) => axesMap[k] !== "secondary");
    const secondaryKeys = yKeys.filter((k) => axesMap[k] === "secondary");
    const leftFmt = cfmts[primaryKeys[0]] || (isCurrency ? "currency" : "number");
    const rightFmt = cfmts[secondaryKeys[0]] || "percent";
    const hasRight = secondaryKeys.length > 0;
    // RIGHT-AXIS DOMAIN FIX: a sub-1% CTR fraction (max ~0.0032) left the axis to recharts'
    // auto-scale, which produced a nonsensical 0..400% domain (and a flat-looking line). Pin the
    // secondary axis to its OWN series' real range [0, max*1.15] in the series' native units
    // (fraction for 'percent', points for 'percent_scaled'), so the ticks — formatted by the same
    // formatMetric — read e.g. 0% .. 0.37%. Fall back to recharts' auto-domain only if we can't
    // measure the series (no numeric values).
    let rightDomain;
    if (hasRight) {
      let maxRight = 0, sawNum = false;
      for (const row of chartData) {
        for (const k of secondaryKeys) {
          const n = Number(row[k]);
          if (!isNaN(n) && isFinite(n)) { sawNum = true; if (n > maxRight) maxRight = n; }
        }
      }
      // 15% headroom; guard an all-zero/degenerate series so the axis never collapses to [0,0].
      if (sawNum) rightDomain = [0, maxRight > 0 ? maxRight * 1.15 : 1];
    }
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: bandTopMargin, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          {hasBands && <Customized component={CompanyBands} />}
          <XAxis dataKey={xKey} fontSize={11} tick={{ fill: "#64748b" }} tickFormatter={fmtXTick} interval={chartData.length > 12 ? "preserveStartEnd" : 0} angle={chartData.length > 3 ? -30 : 0} textAnchor={chartData.length > 3 ? "end" : "middle"} height={chartData.length > 3 ? 74 : 30} />
          <YAxis yAxisId="left" fontSize={11} tick={{ fill: "#64748b" }} tickFormatter={(v) => fmtBy(leftFmt, v)} />
          {hasRight && <YAxis yAxisId="right" orientation="right" fontSize={11} tick={{ fill: "#64748b" }} domain={rightDomain || undefined} tickFormatter={(v) => fmtBy(rightFmt, v)} />}
          <Tooltip formatter={(val, name) => fmtBy(cfmts[name] || leftFmt, val)} labelFormatter={fmtXTick} contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
          <Legend />
          {yKeys.map((key, i) => {
            const yId = axesMap[key] === "secondary" ? "right" : "left";
            if (seriesTypes[key] === "line") {
              return <Line key={key} yAxisId={yId} type="monotone" dataKey={key} stroke={col(i)} strokeWidth={2} dot={{ r: 3 }}
                label={chartData.length <= 24 ? { position: "top", fontSize: 9, fill: "#64748b", formatter: (val) => fmtBy(cfmts[key] || rightFmt, val) } : undefined} />;
            }
            return <Bar key={key} yAxisId={yId} dataKey={key} fill={col(i)} stackId={stacks[key] || undefined} radius={[4, 4, 0, 0]}
              label={chartData.length <= 24 ? { position: stacks[key] ? "center" : "top", fontSize: 9, fill: stacks[key] ? "#fff" : "#475569", formatter: (val) => fmtBy(cfmts[key] || leftFmt, val) } : undefined} />;
          })}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  if (type === "pie" || type === "donut") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData} dataKey={yKeys[0]} nameKey={xKey}
            cx="50%" cy="50%"
            outerRadius={90} innerRadius={type === "donut" ? 50 : 0}
            label={({ name, percent }) => `${fmtXTick(name)} (${(percent * 100).toFixed(0)}%)`}
            labelLine={false}
          >
            {chartData.map((_, i) => <Cell key={i} fill={col(i)} />)}
          </Pie>
          <Tooltip formatter={fmtTooltip} labelFormatter={fmtXTick} contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (type === "line") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={xKey} fontSize={11} tick={{ fill: "#64748b" }} tickFormatter={fmtXTick} />
          <YAxis fontSize={11} tick={{ fill: "#64748b" }} tickFormatter={fmtAxis} />
          <Tooltip formatter={fmtTooltip} labelFormatter={fmtXTick} contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
          <Legend />
          {yKeys.map((key, i) => (
            <Line key={key} type="monotone" dataKey={key} stroke={col(i)} strokeWidth={2} dot={{ r: 3 }}
              label={yKeys.length === 1 && chartData.length <= 24 ? { position: "top", fontSize: 9, fill: "#64748b", formatter: fmtAxis } : undefined} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (type === "area") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
          <defs>
            {yKeys.map((key, i) => (
              <linearGradient key={key} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={col(i)} stopOpacity={0.6} />
                <stop offset="95%" stopColor={col(i)} stopOpacity={0.05} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={xKey} fontSize={11} tick={{ fill: "#64748b" }} tickFormatter={fmtXTick} />
          <YAxis fontSize={11} tick={{ fill: "#64748b" }} tickFormatter={fmtAxis} />
          <Tooltip formatter={fmtTooltip} labelFormatter={fmtXTick} contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
          <Legend />
          {yKeys.map((key, i) => (
            <Area key={key} type="monotone" dataKey={key} stroke={col(i)} fill={`url(#grad-${i})`} strokeWidth={2}
              label={yKeys.length === 1 && chartData.length <= 24 ? { position: "top", fontSize: 9, fill: "#64748b", formatter: fmtAxis } : undefined} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // Default: bar chart (supports stacked via stack_id)
  // Sign-color bars red/green when a single series contains negative values
  // (e.g. variance / gain-loss / "key movers" charts). Purely-positive series keep brand colors.
  const POS_GREEN = "#16a34a", NEG_RED = "#dc2626";
  const signColorBars = yKeys.length === 1 && chartData.some(d => Number(d[yKeys[0]]) < 0);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} margin={{ top: bandTopMargin, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        {hasBands && <Customized component={CompanyBands} />}
        <XAxis dataKey={xKey} fontSize={11} tick={{ fill: "#64748b" }} tickFormatter={fmtXTick} interval={chartData.length > 12 ? "preserveStartEnd" : 0} angle={chartData.length > 3 ? -30 : 0} textAnchor={chartData.length > 3 ? "end" : "middle"} height={chartData.length > 3 ? 74 : 30} />
        <YAxis fontSize={11} tick={{ fill: "#64748b" }} tickFormatter={fmtAxis} />
        <Tooltip formatter={fmtTooltip} labelFormatter={fmtXTick} contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
        <Legend />
        {yKeys.map((key, i) => (
          <Bar key={key} dataKey={key} fill={col(i)} radius={[4, 4, 0, 0]}
            stackId={type === "stacked_bar" ? "stack" : undefined}
            label={(yKeys.length === 1 || type === "stacked_bar") && chartData.length <= 24 ? { position: type === "stacked_bar" ? "center" : "top", fontSize: 9, fill: type === "stacked_bar" ? "#fff" : "#475569", formatter: fmtAxis } : undefined}
          >
            {signColorBars && chartData.map((d, idx) => (
              <Cell key={idx} fill={Number(d[key]) < 0 ? NEG_RED : POS_GREEN} />
            ))}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
